<template>
  <div class="container-paymentautomatic">
    <div class="wrapper-payment">
      <div class="tit-h5">{{ t('auto_renew_prompt') }}</div>

      <label class="checkbox-wrap">
        <input type="checkbox" :class="{ 'input-error': isBaseError }" v-model="rechargeAllow" />
        <div class="checkbox-ui">
          <IconCheck />
        </div>
        <p>{{ t('auto_renew_description') }}</p>
      </label>

      <div class="error-message" v-if="!rechargeAllow">
        <img src="/icons/caution.svg" alt="" />
        <p class="small error">
          {{ t('ss_warning_no_refill') }}
        </p>
      </div>
    </div>

    <div class="wrapper-payment">
      <p :class="['gd-txt', { dimmer: !rechargeAllow }]">
        {{ t('auto_renew_trigger_amount') }}
      </p>
      <div class="input-with-unit" style="--unit: 'Credit'" :class="{ disabled: !rechargeAllow }">
        <InputNumberWithComma
          v-model.number="form.rechargeBaseAmount"
          :has-error="isBaseError"
          :value-min="0"
          :is-disabled="!rechargeAllow"
          :is-allowed-decimal="false"
        />
      </div>

      <div v-if="isBaseError" class="error-message">
        <IconInfo class="icon-smaller icon-error" />
        <p class="small error">
          {{
            t('ss_error_recharge_base', {
              minimum: MINIMUM_RECHARGE.toLocaleString(),
              maximum: MAXIMUM_RECHARGE.toLocaleString(),
            })
          }}
        </p>
      </div>
    </div>

    <div class="wrapper-payment">
      <p :class="['gd-txt', { dimmer: !rechargeAllow }]">
        {{ t('auto_renew_recharge_amount') }}
      </p>
      <div class="input-with-unit" style="--unit: 'Credit'" :class="{ disabled: !rechargeAllow }">
        <InputNumberWithComma
          v-model.number="form.rechargeUnitAmount"
          :has-error="isUnitError"
          :value-min="0"
          :is-disabled="!rechargeAllow"
          :is-allowed-decimal="false"
        />
      </div>

      <p v-if="maxAutoRechargeAmount">{{ t('ss_refill_unit_payment_max') }} {{ maxAutoRechargeAmount }}</p>
      <div v-if="isUnitError" class="error-message">
        <IconInfo class="icon-smaller icon-error" />
        <p class="small error">
          {{
            t('ss_error_recharge_unit', {
              minimum: MINIMUM_RECHARGE.toLocaleString(),
              maximum: MAXIMUM_RECHARGE.toLocaleString(),
            })
          }}
        </p>
      </div>
    </div>

    <div class="wrapper-payment">
      <p :class="['gd-txt', { dimmer: !rechargeAllow }]">
        {{ t('auto_renew_monthly_limit') }}
      </p>
      <div class="input-with-unit" style="--unit: 'Credit'" :class="{ disabled: !rechargeAllow }">
        <InputNumberWithComma
          v-model.number="form.rechargeLimitAmount"
          :has-error="isLimitError"
          :value-min="0"
          :is-disabled="!rechargeAllow"
          :is-allowed-decimal="false"
        />
      </div>

      <div v-if="isLimitError" class="error-message">
        <IconInfo class="icon-smaller icon-error" />
        <p class="small error">
          {{
            t('ss_error_recharge_limit', {
              minimum: form.rechargeUnitAmount?.toLocaleString() ?? MINIMUM_RECHARGE.toLocaleString(),
            })
          }}
        </p>
      </div>
    </div>
  </div>

  <div class="footer">
    <ButtonProcess secondary @click="closePopup">
      {{ t('cancel') }}
    </ButtonProcess>

    <ButtonProcess :disabled="isDisabledButton" @click="handleSave">
      {{ saveButtonLabel }}
    </ButtonProcess>
  </div>
</template>

<script setup lang="ts">
import { subscriptionChangeRenew } from '@/api/apiService';
import IconCheck from '@/components/icons/IconCheck.vue';
import IconInfo from '@/components/icons/IconInfo.vue';
import ButtonProcess from '@/components/shared/base/ButtonProcess.vue';
import InputNumberWithComma from '@/components/shared/base/InputNumberWithComma.vue';
import { useGtag } from '@/composables/shared/useGtag';
import { usePlan } from '@/composables/shared/usePlan';
import { usePopupStore } from '@/stores/popup';
import { useSubscriptionStore } from '@/stores/subscription';
import { useToastStore } from '@/stores/toast';
import { MINIMUM_RECHARGE, MAXIMUM_RECHARGE } from '@/utils/constants/payment';

const { t } = useI18n();

const toastStore = useToastStore();
const popupStore = usePopupStore();
const subscriptionStore = useSubscriptionStore();

const { getUserPlanCreditPerPrice, calcCeilPrice } = usePlan();
const { trackEventFunnel } = useGtag();

const rechargeAllow = ref(subscriptionStore.currentPlanDetails?.rechargeAllow ?? false);
const form = ref<Record<string, number>>({
  rechargeBaseAmount: subscriptionStore.currentPlanDetails?.rechargeBaseAmount ?? 0,
  rechargeUnitAmount: subscriptionStore.currentPlanDetails?.rechargeUnitAmount ?? 0,
  rechargeLimitAmount: subscriptionStore.currentPlanDetails?.rechargeLimitAmount ?? 0,
});

const pricePerCredit = ref<number>(0);
const rechargeError = ref<string>('');

const isBaseError = computed<boolean>(() => {
  if (!rechargeError.value) return false;

  return rechargeError.value === 'rechargeBaseAmount';
});

const isUnitError = computed<boolean>(() => {
  if (!rechargeError.value) return false;

  return rechargeError.value === 'rechargeUnitAmount';
});

const isLimitError = computed<boolean>(() => {
  if (!rechargeError.value) return false;

  return rechargeError.value === 'rechargeLimitAmount';
});

const isDisabledButton = computed(() => {
  const { rechargeBaseAmount, rechargeUnitAmount, rechargeLimitAmount } = form.value;
  return !rechargeAllow.value || !rechargeBaseAmount || !rechargeUnitAmount || !rechargeLimitAmount;
});

const maxAutoRechargeAmount = computed(() => {
  if (!form.value.rechargeUnitAmount) {
    return '';
  }

  const price = calcCeilPrice(form.value.rechargeUnitAmount, subscriptionStore.currency, pricePerCredit.value);

  return subscriptionStore.currency === 'KRW'
    ? `${price.toLocaleString()} ${t('label_currency_won')}`
    : `$${price.toLocaleString('en-US')}`;
});

const saveButtonLabel = computed(() => {
  return rechargeAllow.value ? t('label_change') : t('button_save2');
});

const closePopup = () => {
  popupStore.closeLast();
};

const handleSave = async () => {
  const isValid = validateForm();
  if (!isValid) {
    return;
  }

  trackEventFunnel('click_auto_recharge_setting_button');

  const { paymentType, plan, id } = subscriptionStore.currentPlanDetails!;
  const params = {
    ...form.value,
    plan,
    paymentType,
    rechargeAllow: rechargeAllow.value,
  };

  const { success, errorCode } = await subscriptionChangeRenew(id, params);
  if (!success) {
    switch (errorCode) {
      case 8003:
        rechargeError.value = 'rechargeBaseAmount';
        return;
      case 8004:
        rechargeError.value = 'rechargeUnitAmount';
        return;
      case 8005:
        rechargeError.value = 'rechargeLimitAmount';
        return;
    }

    return;
  }

  const message = subscriptionStore.currentPlanDetails?.rechargeAllow
    ? t('auto_refill_success_update')
    : t('auto_refill_success');
  toastStore.info(message);
  trackEventFunnel('complete_auto_recharge_setting');

  await subscriptionStore.fetchSubscriptionInfo();
  closePopup();
};

const validateForm = () => {
  if (rechargeError.value) {
    rechargeError.value = '';
  }

  for (const [key, value] of Object.entries(form.value)) {
    if (value && value < MINIMUM_RECHARGE) {
      rechargeError.value = key;
    }

    if (key === 'rechargeBaseAmount' && value > MAXIMUM_RECHARGE) {
      rechargeError.value = key;
      return false;
    }

    if (key === 'rechargeLimitAmount' && form.value.rechargeUnitAmount > value) {
      rechargeError.value = key;
      return false;
    }
  }

  return true;
};

onMounted(async () => {
  const result = (await getUserPlanCreditPerPrice('PRO', subscriptionStore.currency)) ?? [];
  pricePerCredit.value = result[0];
});
</script>

<style scoped lang="scss">
.footer {
  display: flex;
  gap: 0.5rem;
  background: #fff;
  padding-top: 40px;
  justify-content: flex-end;
}

@include respond-to(mobile-portrait) {
  :deep(.modal-content) {
    height: 100dvh;
    border-radius: 0;
    main {
      justify-content: space-between;
      height: 100%;
      .container-paymentautomatic {
        overflow-y: auto;
        &::-webkit-scrollbar {
          display: none;
        }
      }
    }
  }
}

.container-paymentautomatic {
  .error-message {
    width: 100%;
    display: flex;
    justify-content: left;
    margin-top: 0.5rem;
    padding: $size-m;
    word-break: keep-all;
    gap: 12px;
    border-radius: 8px;
    background-color: $error-bg;
    .icon-error {
      position: relative;
      top: 0.125rem;
    }
  }
  .tit-h5 {
    line-height: 140%;
    margin-bottom: 1.5rem;
  }
}

.wrapper-payment {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1.5rem;
  p {
    font-size: $font-size-psmall;
    word-break: break-all;
  }
  .gd-txt {
    font-size: $font-size-psmall;
    position: relative;
    &::after {
      content: '';
      position: absolute;
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: $new-warning;
      top: 2px;
    }
  }
}

.input-with-unit {
  position: relative;
  display: inline-block;
  width: 100%;
}

.input-with-unit > input {
  width: 100%;
  padding-right: 4.5rem;
}

.input-with-unit::after {
  content: var(--unit);
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
  font-size: 0.9em;
  pointer-events: none;
}

.input-with-unit.disabled::after {
  opacity: 0.5;
}

input[type='checkbox'] {
  display: none;
}

.checkbox-wrap {
  p {
    font-size: $font-size-psmall;
  }
  .checkbox-ui {
    position: relative;
    width: 1.125rem;
    height: 1.125rem;
    background-color: $color-gray-50;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
    border-radius: 0.25rem;
    svg {
      color: #fff;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
  input:checked + .checkbox-ui {
    background-color: var(--surface-primary);
    border-color: var(--surface-primary);
  }

  input:disabled + .checkbox-ui {
    background-color: var(--surface-gray);
    border-color: var(--border-default);
  }
}
</style>
